// components/AccountSettingsPage.tsx — modal-ish account settings surface.
//
// Phase 3 addition. Currently hosts only the Delete-my-account flow; the
// page is shaped to host future account-level toggles (notification
// preferences, default scenario rebind, etc.) when they land.
//
// Two states for the delete action:
//   - idle:        button → confirmation prompt
//   - pending:     scheduledAt banner + "I changed my mind" → cancel
//
// The mount-time `useEffect` polls /api/account/deletion-status once so a
// session that lands mid-cooldown shows the banner without waiting for a
// fresh DELETE round-trip.

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  cancelAccountDeletion,
  getAccountDeletionStatus,
  scheduleAccountDeletion,
} from "../services/referralsApi";
import { updateLanguage } from "../services/authApi";
import {
  SUPPORTED_LANGUAGES,
  findSupportedLanguage,
} from "../i18n/supportedLanguages";
import { loadLanguage } from "../i18n";

export interface AccountSettingsPageProps {
  onClose: () => void;
  onToast: (text: string, durationMs?: number) => void;
  /**
   * Phase A i18n — the user's current account-level narration language
   * (BCP-47 code, or null when never set / English default). Drives the
   * Language picker selection. Null displays "English (default)" and
   * is functionally equivalent to "en" at the server boundary.
   */
  currentLanguage: string | null;
  /**
   * Phase A i18n — called when the user picks a new language. The
   * component fires PUT /api/auth/language itself; this callback is for
   * the parent to mirror the new code into local state so dependent
   * surfaces (ScenarioPicker, user-menu sub-row label) update at once.
   */
  onLanguageChanged: (code: string) => void;
}

type DeletionState =
  | { status: "loading" }
  | { status: "idle" }
  | { status: "confirming" }
  | { status: "submitting" }
  | { status: "pending"; scheduledAt: string }
  | { status: "error"; message: string };

export default function AccountSettingsPage({
  onClose,
  onToast,
  currentLanguage,
  onLanguageChanged,
}: AccountSettingsPageProps) {
  const { t } = useTranslation(["referrals", "common"]);
  const [state, setState] = useState<DeletionState>({ status: "loading" });
  // Phase A i18n — local mirror of the current language so the dropdown
  // reflects an in-progress change immediately. We send to the server in
  // parallel; on failure we revert + toast.
  const [language, setLanguage] = useState<string>(currentLanguage ?? "en");
  const [languageSaving, setLanguageSaving] = useState(false);

  // Sync local mirror with prop whenever the parent's view changes (e.g.
  // the first-visit detection effect set it after mount).
  useEffect(() => {
    setLanguage(currentLanguage ?? "en");
  }, [currentLanguage]);

  // Initial status fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAccountDeletionStatus();
        if (cancelled) return;
        if (res.pending && res.scheduledAt) {
          setState({ status: "pending", scheduledAt: res.scheduledAt });
        } else {
          setState({ status: "idle" });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const confirmDelete = useCallback(async () => {
    setState({ status: "submitting" });
    try {
      const res = await scheduleAccountDeletion();
      setState({ status: "pending", scheduledAt: res.scheduledAt });
      onToast(t("common:toasts.deletionScheduled"), 7000);
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [onToast, t]);

  const handleLanguagePick = useCallback(
    async (code: string) => {
      if (code === language) return;
      const previous = language;
      setLanguage(code);
      setLanguageSaving(true);
      try {
        await updateLanguage(code);
        // Lazy-load and activate the new language bundle so the UI
        // re-renders in the target language immediately. Best-effort —
        // missing-bundle warnings are logged but never surface to the user.
        await loadLanguage(code);
        onLanguageChanged(code);
        const entry = findSupportedLanguage(code);
        onToast(
          t("common:toasts.languageSet", { name: entry?.nativeName ?? code }),
          5000,
        );
      } catch (err) {
        // Revert the dropdown if the PUT failed — the cached state is
        // ahead of the server otherwise.
        setLanguage(previous);
        const msg = err instanceof Error ? err.message : String(err);
        onToast(
          t("common:toasts.languageChangeFailed", { message: msg }),
          6000,
        );
      } finally {
        setLanguageSaving(false);
      }
    },
    [language, onLanguageChanged, onToast, t],
  );

  const undoDelete = useCallback(async () => {
    try {
      await cancelAccountDeletion();
      setState({ status: "idle" });
      onToast(t("common:toasts.deletionCancelled"), 5000);
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [onToast, t]);

  return (
    <div style={backdrop} role="dialog" aria-modal="true">
      <div style={card}>
        <header style={cardHeader}>
          <h2 style={titleStyle}>{t("referrals:account.title")}</h2>
          <button
            type="button"
            style={closeBtn}
            onClick={onClose}
            aria-label={t("common:buttons.close")}
          >
            ×
          </button>
        </header>

        <div style={body}>
          <section>
            <h3 style={sectionTitle}>{t("referrals:account.languageSection")}</h3>
            <p style={sectionDesc}>{t("referrals:account.languageDesc")}</p>
            <select
              value={language}
              onChange={(e) => void handleLanguagePick(e.target.value)}
              disabled={languageSaving}
              aria-label={t("referrals:account.languageAria")}
              style={languageSelect}
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.nativeName}
                  {l.code !== "en" ? ` — ${l.name}` : ""}
                </option>
              ))}
            </select>
          </section>

          <section>
            <h3 style={sectionTitle}>{t("referrals:account.deleteSection")}</h3>
            <p style={sectionDesc}>{t("referrals:account.deleteDesc")}</p>
            <DeletionRow
              state={state}
              onStart={() => setState({ status: "confirming" })}
              onConfirm={confirmDelete}
              onCancel={() => setState({ status: "idle" })}
              onUndo={undoDelete}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

function DeletionRow({
  state,
  onStart,
  onConfirm,
  onCancel,
  onUndo,
}: {
  state: DeletionState;
  onStart: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onUndo: () => void;
}) {
  const { t } = useTranslation(["referrals", "common"]);
  if (state.status === "loading") {
    return <div style={loadingHint}>{t("referrals:account.checkingStatus")}</div>;
  }
  if (state.status === "error") {
    return (
      <div style={errorBanner}>
        {t("referrals:account.somethingWentWrong", { message: state.message })}
      </div>
    );
  }
  if (state.status === "pending") {
    // The pendingDesc translation contains a literal `<strong>{{date}}</strong>`
    // tag; split-render so the date is inside an inline <strong>.
    const raw = t("referrals:account.pendingDesc", {
      date: formatDate(state.scheduledAt),
    });
    const before = raw.split("<strong>")[0];
    const inside = raw.split("<strong>")[1]?.split("</strong>")[0] ?? formatDate(state.scheduledAt);
    const after = raw.split("</strong>")[1] ?? "";
    return (
      <div style={pendingBanner}>
        <div style={pendingTitle}>{t("referrals:account.pendingTitle")}</div>
        <div style={pendingDesc}>
          {before}
          <strong>{inside}</strong>
          {after}
        </div>
        <div style={{ marginTop: 12 }}>
          <button type="button" style={primaryAction} onClick={onUndo}>
            {t("referrals:account.changedMyMind")}
          </button>
        </div>
      </div>
    );
  }
  if (state.status === "confirming") {
    return (
      <div style={confirmBanner}>
        <p style={confirmText}>{t("referrals:account.confirmText")}</p>
        <div style={confirmActions}>
          <button
            type="button"
            style={destructiveAction}
            onClick={onConfirm}
          >
            {t("referrals:account.scheduleDeletion")}
          </button>
          <button type="button" style={secondaryAction} onClick={onCancel}>
            {t("common:buttons.cancel")}
          </button>
        </div>
      </div>
    );
  }
  if (state.status === "submitting") {
    return <div style={loadingHint}>{t("referrals:account.scheduling")}</div>;
  }
  // idle
  return (
    <button type="button" style={destructiveLink} onClick={onStart}>
      {t("referrals:account.deleteAccount")}
    </button>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// ── Styles ──────────────────────────────────────────────────────────────

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 6, 4, 0.78)",
  zIndex: 200,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 16px",
  overflowY: "auto",
};

const card: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.92))",
  color: "var(--ink, #e8d9b8)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  maxWidth: 640,
  width: "100%",
  padding: 0,
  boxShadow: "0 28px 70px rgba(0,0,0,0.7)",
  fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
};

const cardHeader: CSSProperties = {
  padding: "20px 28px 14px",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: "1.4rem",
  fontWeight: 500,
  color: "var(--accent, #d4a574)",
  letterSpacing: "0.02em",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "1.4rem",
  width: 32,
  height: 32,
  borderRadius: 2,
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
};

const body: CSSProperties = {
  padding: "20px 28px",
  display: "flex",
  flexDirection: "column",
  gap: 22,
};

const sectionTitle: CSSProperties = {
  margin: "0 0 6px",
  fontSize: "0.75rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--ink-muted, #8a7355)",
  fontFamily: "'JetBrains Mono', monospace",
};

const sectionDesc: CSSProperties = {
  margin: "0 0 12px",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "0.92rem",
  lineHeight: 1.55,
};

const loadingHint: CSSProperties = {
  fontStyle: "italic",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "0.9rem",
};

const errorBanner: CSSProperties = {
  padding: 12,
  border: "1px solid rgba(224,133,133,0.4)",
  background: "rgba(224,133,133,0.08)",
  color: "#e08585",
  fontSize: "0.88rem",
  borderRadius: 2,
};

const pendingBanner: CSSProperties = {
  padding: 14,
  border: "1px solid rgba(232,193,128,0.4)",
  background: "rgba(232,193,128,0.08)",
  borderRadius: 2,
};

const pendingTitle: CSSProperties = {
  color: "#e8c180",
  fontStyle: "italic",
  fontSize: "1.05rem",
};

const pendingDesc: CSSProperties = {
  marginTop: 6,
  color: "var(--ink, #e8d9b8)",
  fontSize: "0.92rem",
  lineHeight: 1.55,
};

const confirmBanner: CSSProperties = {
  padding: 14,
  border: "1px solid rgba(224,133,133,0.4)",
  background: "rgba(224,133,133,0.06)",
  borderRadius: 2,
};

const confirmText: CSSProperties = {
  margin: 0,
  color: "var(--ink, #e8d9b8)",
  fontSize: "0.92rem",
  lineHeight: 1.55,
};

const confirmActions: CSSProperties = {
  marginTop: 10,
  display: "flex",
  gap: 10,
};

const primaryAction: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  color: "var(--accent, #d4a574)",
  padding: "8px 14px",
  fontSize: "0.85rem",
  borderRadius: 2,
  fontFamily: "inherit",
  cursor: "pointer",
};

const secondaryAction: CSSProperties = {
  ...primaryAction,
  color: "var(--ink-muted, #8a7355)",
};

const destructiveAction: CSSProperties = {
  ...primaryAction,
  borderColor: "rgba(224,133,133,0.5)",
  color: "#e08585",
};

const languageSelect: CSSProperties = {
  background: "rgba(28,20,14,0.7)",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.25))",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "inherit",
  fontSize: "0.95rem",
  padding: "8px 12px",
  borderRadius: 2,
  cursor: "pointer",
  minWidth: 240,
};

const destructiveLink: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(224,133,133,0.35)",
  color: "#e08585",
  padding: "8px 14px",
  fontSize: "0.88rem",
  borderRadius: 2,
  fontFamily: "inherit",
  cursor: "pointer",
  fontStyle: "italic",
};
